import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatMoney } from '../Shared/Formatters';

function fmtDate(d) {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: '2-digit',
        });
    } catch {
        return '—';
    }
}

function statusClass(status) {
    if (!status) return 'other';
    const s = String(status).toLowerCase();
    if (s === 'live') return 'live';
    if (s === 'uat') return 'uat';
    return 'other';
}

/**
 * TenantProfileCard — Sprint 3.9
 *
 * Replaces the profile half of the legacy WidgetsWraper.jsx.
 * Pure presentational: feed it the response of GET /api/v3/tenants/:id.
 */
export default function TenantProfileCard({ tenant, liveDays, lifetime, lifetimeLoading }) {
    const { t } = useTranslation();
    if (!tenant) return null;

    const cls = statusClass(tenant.status);
    const sinceLabel = lifetime?.since ? t('Since {{date}}', { date: fmtDate(lifetime.since) }) : t('All recorded sales');

    return (
        <div className="v3-td-card">
            <div className="v3-td-profile">
                <div className="v3-td-profile-head">
                    <div>
                        <h2 className="v3-td-profile-name">{tenant.store_name || '—'}</h2>
                        {tenant.brand_name && tenant.brand_name !== tenant.store_name && (
                            <p className="v3-td-profile-brand">{tenant.brand_name}</p>
                        )}
                    </div>
                    {tenant.status && (
                        <span className={`v3-td-status ${cls}`}>{tenant.status}</span>
                    )}
                </div>

                <div className="v3-td-lifetime">
                    <div className="v3-td-lifetime-cell">
                        <span className="lbl">{t('Total sales recorded')}</span>
                        <span className="val">
                            {lifetimeLoading && !lifetime
                                ? <span className="v3-td-skeleton" style={{ display: 'inline-block', height: 20, width: 110 }}>&nbsp;</span>
                                : formatMoney(lifetime?.totalSales)}
                        </span>
                        <span className="v3-td-lifetime-since">{sinceLabel}</span>
                    </div>
                    <div className="v3-td-lifetime-cell">
                        <span className="lbl">{t('Total revenue generated')}</span>
                        <span className="val">
                            {lifetimeLoading && !lifetime
                                ? <span className="v3-td-skeleton" style={{ display: 'inline-block', height: 20, width: 110 }}>&nbsp;</span>
                                : formatMoney(lifetime?.totalRevenue)}
                        </span>
                        <span className="v3-td-lifetime-since">{sinceLabel}</span>
                    </div>
                </div>

                <div className="v3-td-profile-grid">
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Tenant code')}</span>
                        <span className="val" title={tenant.code}>{tenant.code || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Unit code')}</span>
                        <span className="val" title={tenant.unit_code}>{tenant.unit_code || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Property')}</span>
                        <span className="val" title={tenant.property_name}>{tenant.property_name || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Floor')}</span>
                        <span className="val">{tenant.floor || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Group')}</span>
                        <span className="val" title={tenant.group_name}>{tenant.group_name || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Category')}</span>
                        <span className="val" title={tenant.category}>{tenant.category || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Subcategory')}</span>
                        <span className="val" title={tenant.subcategory}>{tenant.subcategory || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Store type')}</span>
                        <span className="val">{tenant.store_type || '—'}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Lease start')}</span>
                        <span className="val">{fmtDate(tenant.lease_startdate)}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Lease end')}</span>
                        <span className="val">{fmtDate(tenant.lease_enddate)}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Live date')}</span>
                        <span className="val">{fmtDate(tenant.live_date)}</span>
                    </div>
                    <div className="v3-td-meta">
                        <span className="lbl">{t('Live days')}</span>
                        <span className="val">{liveDays != null ? formatNumber(liveDays) : '—'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
